// (c) chaehongee. Confidential and proprietary.

import { useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X as CloseIcon } from 'lucide-react'

import { getBillingConfig, type BillingConfig } from '../../../services/billing'
import { useModalDialog } from '../../../hooks/useModalDialog'
import { CreditPurchaseForm } from '../../shared/CreditPurchaseForm'

import styles from './CreditRechargeModal.module.css'

interface CreditRechargeModalProps {
    isOpen: boolean
    onClose: () => void
}

export function CreditRechargeModal({
    isOpen,
    onClose,
}: CreditRechargeModalProps): ReactElement | null {
    const { t } = useTranslation('billing')
    const { dialogRef } = useModalDialog(isOpen, onClose)
    const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null)
    const [configError, setConfigError] = useState<string | null>(null)

    useEffect(() => {
        if (!isOpen) {
            setConfigError(null)
            return
        }
        void (async () => {
            try {
                const config = await getBillingConfig()
                setBillingConfig(config)
            } catch {
                setConfigError(t('purchaseFailed'))
            }
        })()
    }, [isOpen, t])

    if (!isOpen) return null

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="creditRechargeTitle"
                tabIndex={-1}
                className={styles.modal}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={styles.header}>
                    <h2 id="creditRechargeTitle" className={styles.title}>
                        {t('rechargeCredits')}
                    </h2>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={t('common:close')}
                    >
                        <CloseIcon size={20} />
                    </button>
                </header>

                <div className={styles.content}>
                    {configError && (
                        <div className={styles.error}>{configError}</div>
                    )}
                    {billingConfig && (
                        <CreditPurchaseForm billingConfig={billingConfig} />
                    )}
                    {!billingConfig && !configError && (
                        <p className={styles.description}>{t('common:loading')}</p>
                    )}
                </div>
            </div>
        </div>
    )
}
